import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Sidebar } from './components/layout/Sidebar';
import { Topbar } from './components/layout/Topbar';
import { Toast } from './components/common/Toast';
import { LeadDrawer } from './components/leads/LeadDrawer';

import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Leads } from './pages/Leads';
import { Channels } from './pages/Channels';
import { Reports } from './pages/Reports';
import { Chatbot } from './pages/Chatbot';
import { Proposal } from './pages/Proposal';
import { Settings } from './pages/Settings';

import { useLeads } from './hooks/useLeads';

function MainAppLayout() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [toast, setToast] = useState(null);

  const {
    leads,
    updateLead,
    updateStatus,
    updateAssignee,
    addNote,
    createLead,
    deleteLead,
    resetData
  } = useLeads();

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const handleCreateLeadAndSelect = (leadData) => {
    const created = createLead(leadData);
    if (created) {
      setSelectedLead(created);
    }
  };

  return (
    <div className="app-layout">
      {/* Navigation Sidebar */}
      <Sidebar mobileOpen={mobileSidebarOpen} setMobileOpen={setMobileSidebarOpen} />

      {/* Main Content Area */}
      <div className="app-main">
        <Topbar
          onMenuClick={() => setMobileSidebarOpen(true)}
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          onAddLead={handleCreateLeadAndSelect}
          showToast={showToast}
        />

        <main className="app-content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <Dashboard
                  leads={leads}
                  onSelectLead={setSelectedLead}
                  globalSearch={globalSearch}
                />
              }
            />
            <Route
              path="/leads"
              element={
                <Leads
                  leads={leads}
                  onSelectLead={setSelectedLead}
                  onUpdateLead={updateLead}
                  onUpdateStatus={updateStatus}
                  onUpdateAssignee={updateAssignee}
                  onDeleteLead={deleteLead}
                  onAddNote={addNote}
                  showToast={showToast}
                />
              }
            />
            <Route
              path="/channels"
              element={<Channels leads={leads} onSelectLead={setSelectedLead} />}
            />
            <Route path="/reports" element={<Reports />} />
            <Route
              path="/chatbot"
              element={
                <Chatbot
                  onLeadCreated={(newLead) => {
                    setSelectedLead(newLead);
                  }}
                  showToast={showToast}
                />
              }
            />
            <Route path="/proposal" element={<Proposal />} />
            <Route
              path="/settings"
              element={
                <Settings
                  onResetDemoData={() => {
                    resetData();
                    setSelectedLead(null);
                  }}
                  showToast={showToast}
                />
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>

      {/* Global Lead Details Drawer */}
      <LeadDrawer
        lead={selectedLead}
        isOpen={Boolean(selectedLead)}
        onClose={() => setSelectedLead(null)}
        onAddNote={(id, text, author) => {
          addNote(id, text, author);
          showToast('Note added to lead timeline', 'success');
        }}
        onUpdateStatus={(id, status) => {
          updateStatus(id, status);
          showToast(`Status updated to ${status}`, 'success');
        }}
      />

      {/* Global Toast Notification */}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Unprotected Login Page */}
        <Route path="/login" element={<Login />} />

        {/* Protected Application Workspace */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainAppLayout />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

export default App;
